import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, InputAdornment, Popover, TextField } from '@mui/material';
import { AccessTime as AccessTimeIcon } from '@mui/icons-material';
import IosTimeWheel, { IosTimeParts, iosTimeFrom24, iosTimeTo24 } from './IosTimeWheel';
import { formatTime12hFrom24 } from '../utils/easternTime';
import {
  formatVisitDateTimeLabel,
  normalizeFlexibleTimeInput,
  parseFlexibleTimeInput,
  previewFlexibleTimeInput,
  easternWallClockDate,
} from '../utils/visitTime';

const LINE_BG = '#2D4B73';

export interface VisitTimeFieldProps {
  label: string;
  visitDate: string;
  value: string;
  onChange: (canonical: string) => void;
  /** Shown in red when the parent save attempt found this field invalid. */
  errorText?: string;
  /** Extra helper that stays gray. Ignored while `errorText` is set. */
  helperText?: string;
  error?: boolean;
  size?: 'small' | 'medium';
  /** When true, helper includes the visit date once a time is set. */
  showVisitLabel?: boolean;
}

const VisitTimeField: React.FC<VisitTimeFieldProps> = ({
  label,
  visitDate,
  value,
  onChange,
  errorText,
  helperText,
  error = false,
  size = 'medium',
  showVisitLabel = true,
}) => {
  const [draft, setDraft] = useState(value);
  const [editing, setEditing] = useState(false);
  const [inputError, setInputError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [pickerParts, setPickerParts] = useState<IosTimeParts>({ hour12: 9, minute: 0, meridiem: 'AM' });
  const skipBlurCommitRef = useRef(false);
  const fieldRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!editing) {
      setDraft(value);
    }
  }, [value, editing]);

  useEffect(() => {
    if (!pickerOpen) return;
    const parsed = parseFlexibleTimeInput(editing ? draft : value);
    if (!parsed) return;
    setPickerParts(iosTimeFrom24(parsed.h, parsed.m));
  }, [pickerOpen, editing, draft, value]);

  const displayValue = editing ? draft : value;
  const preview = editing ? previewFlexibleTimeInput(draft) : null;

  const commitDraft = () => {
    const result = normalizeFlexibleTimeInput(draft);
    if (!result.ok) {
      setInputError(result.error);
      return;
    }
    setInputError(null);
    onChange(result.canonical);
    setDraft(result.canonical);
    setEditing(false);
  };

  const applyParts = (parts: IosTimeParts) => {
    const { h, m } = iosTimeTo24(parts);
    const canonical = formatTime12hFrom24(h, m);
    setPickerParts(parts);
    setInputError(null);
    setDraft(canonical);
    setEditing(false);
    onChange(canonical);
  };

  const openPicker = () => {
    if (pickerOpen) return;
    const parsed = parseFlexibleTimeInput((editing ? draft : value) || '');
    const now = easternWallClockDate();
    const base = parsed ?? { h: now.getHours(), m: now.getMinutes() };
    const parts = iosTimeFrom24(base.h, base.m);
    setPickerParts(parts);
    if (!parsed) {
      applyParts(parts);
    }
    setAnchorEl(fieldRef.current);
    setPickerOpen(true);
  };

  const shownHelper = (() => {
    if (inputError) return inputError;
    if (errorText) return errorText;
    if (preview && preview !== draft.trim()) return `→ ${preview}`;
    if (showVisitLabel && value) return formatVisitDateTimeLabel(visitDate, value);
    if (helperText) return helperText;
    if (showVisitLabel) return 'Type 930 for 9:30 AM, or scroll the wheels';
    return undefined;
  })();

  return (
    <>
      <TextField
        ref={fieldRef}
        fullWidth
        size={size}
        label={label}
        value={displayValue}
        placeholder="9:30 AM or 930"
        error={error || !!inputError || !!errorText}
        helperText={shownHelper}
        onChange={e => {
          setEditing(true);
          setInputError(null);
          setDraft(e.target.value);
        }}
        onFocus={() => {
          setEditing(true);
          setDraft(value);
          setInputError(null);
          openPicker();
        }}
        onBlur={() => {
          if (skipBlurCommitRef.current) {
            skipBlurCommitRef.current = false;
            return;
          }
          if (editing) {
            commitDraft();
          }
        }}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commitDraft();
            (e.target as HTMLInputElement).blur();
          }
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                size="small"
                aria-label={`Open ${label} picker`}
                edge="end"
                tabIndex={-1}
                onMouseDown={e => {
                  e.preventDefault();
                  skipBlurCommitRef.current = true;
                }}
                onClick={() => openPicker()}
              >
                <AccessTimeIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            '& fieldset': { borderColor: LINE_BG, borderWidth: 2 },
          },
        }}
      />
      <Popover
        open={pickerOpen}
        anchorEl={anchorEl}
        onClose={() => setPickerOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        sx={{ zIndex: theme => theme.zIndex.modal + 2 }}
        PaperProps={{
          onMouseDown: (e: React.MouseEvent) => e.preventDefault(),
          sx: { bgcolor: '#e5e5ea', borderRadius: 2, overflow: 'hidden', mt: 0.5 },
        }}
      >
        <Box>
          <IosTimeWheel value={pickerParts} onChange={applyParts} />
        </Box>
      </Popover>
    </>
  );
};

export default VisitTimeField;
